import { Layout } from 'components/layout';
import { addApolloState, initializeApollo } from 'lib/hooks/apollo-client';
import { GetServerSideProps } from 'next';
import {
  GetPostDocument,
  useGetPostQuery,
  GetPostQueryVariables,
  GetPostQueryResult,
  BookCardDataFragmentDoc,
  useGetFiveRecentPostsQuery,
  GetFiveRecentPostsDocument,
  GetLinkedBooksDocument,
  GetLinkedBooksQueryResult,
  GetLinkedBooksQueryVariables,
  BookCardDataFragment,
} from 'lib/graphql-generated';
import { ApolloQueryResult, gql } from '@apollo/client';
import { RenderContent } from 'components/content-renderer';
import Link from 'next/link';
import { formatDate, parseContentForBooks } from 'lib/utils';
import { BookCard } from 'components/book-card';

const GET_POST = gql`
  query GetPost($slug: String!) {
    posts(where: { _and: [{ slug: { _eq: $slug } }] }) {
      id
      title
      created_at
      slug
      author {
        id
        name
      }
      category_posts {
        category {
          name
          slug
        }
      }
      post_books(
        order_by: { rank_order: asc }
        where: { book: { is_unlisted: { _eq: false } } }
      ) {
        book {
          ...BookCardData
        }
      }
      image
      content
      type
    }
  }
  ${BookCardDataFragmentDoc}
`;

const GET_RECENT_POSTS = gql`
  query GetFiveRecentPosts {
    posts(limit: 5, order_by: { created_at: desc }) {
      slug
      title
      type
      created_at
      author {
        name
      }
    }
  }
`;

const GET_LINKED_BOOKS = gql`
  query GetLinkedBooks($isbns: [bigint!]!) {
    books(
      where: {
        _and: [{ isbn: { _in: $isbns } }, { is_unlisted: { _eq: false } }]
      }
    ) {
      ...BookCardData
    }
  }
  ${BookCardDataFragmentDoc}
`;
interface Props {
  slug: string;
  linkedBooks: BookCardDataFragment[];
}

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const client = initializeApollo();

  try {
    const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug;

    const variables: GetPostQueryVariables = {
      slug: slug,
    };
    const { data, error }: ApolloQueryResult<GetPostQueryResult['data']> =
      await client.query({
        query: GetPostDocument,
        variables: variables,
      });

    const {
      data: linkedBooks,
    }: ApolloQueryResult<GetLinkedBooksQueryResult['data']> =
      await client.query({
        query: GetLinkedBooksDocument,
        variables: {
          isbns: parseContentForBooks(data.posts[0].content),
        } as GetLinkedBooksQueryVariables,
      });

    const allLinkedBooks: BookCardDataFragment[] = []
      .concat(linkedBooks.books)
      .concat(data.posts[0].post_books.map((b) => b.book));

    if (data.posts) {
      await client.query({
        query: GetFiveRecentPostsDocument,
      });

      return addApolloState(client, {
        props: {
          slug: slug,
          linkedBooks: allLinkedBooks.filter(
            (b, i) =>
              allLinkedBooks.findIndex(
                (bookIndex) => bookIndex.isbn === b.isbn
              ) === i
          ),
        } as Props,
      });
    } else {
      return {
        notFound: true,
      };
    }
  } catch (e) {
    return {
      notFound: true,
    };
  }
};

export const PostPage = ({ slug, linkedBooks }: Props) => {
  const {
    data: { posts },
    loading,
  } = useGetPostQuery({
    variables: {
      slug: slug,
    },
  });
  const post = posts[0];

  const {
    data: { posts: recentPosts },
  } = useGetFiveRecentPostsQuery();

  return (
    <>
      <Layout pageName={post.title} className='bg-green-default'>
        <article className='px-4 md:px-8 2xl:px-2 max-w-screen-2xl mx-auto py-8 md:py-16 flex flex-row flex-wrap gap-12'>
          <header className='flex flex-col md:flex-row w-full gap-4 md:gap-0'>
            <section className='flex-1 my-auto'>
              <h1 className='header text-white text-5xl text-center md:text-left'>
                {post.title}
              </h1>
              <p className='text-white uppercase text-lg text-center md:text-left'>
                {post.author.name}
              </p>
            </section>
            <a
              className='bg-yellow-light rounded mb-auto mx-auto px-2 text-sm text-center'
              href='#linkedBooks'>
              View Linked Books
            </a>
          </header>
          <section className='flex flex-col md:flex-row gap-16'>
            <aside className='relative group mb-auto mx-auto '>
              {post.type === 'review' ? (
                <BookCard
                  book={post.post_books[0].book}
                  className='p-4 max-w-xs  max-h-72'
                />
              ) : post.image ? (
                <img className=' max-h-72' src={post?.image} loading='lazy' />
              ) : (
                <img
                  className=' max-h-72'
                  src={post.post_books[0]?.book?.image}
                  loading='lazy'
                />
              )}
              <svg className='h-12 w-20 absolute -left-8 -top-6 transform group-hover:-rotate-12 ease-in transition-transform '>
                <use href={`#${post.type}`} />
              </svg>
              <section className='mt-12'>
                <h2 className='header text-2xl text-center md:text-left'>
                  Categories
                </h2>
                <ul className='flex flex-row flex-wrap lg:flex-col text-sm justify-center'>
                  {post.category_posts.map(({ category }, index) => (
                    <li
                      className='mr-2 mb-2'
                      key={`${index}_linked_categories`}>
                      <Link href={`/browse/${category.slug}`}>
                        <a className='bg-white rounded-full text-green-dark px-2 py-1 mr-auto border border-green-default hover:border-white hover:bg-green-light hover:text-white inline-flex'>
                          {category.name}
                        </a>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            </aside>
            <section className='bg-white rounded mx-auto w-full px-12 py-6 flex flex-col'>
              <div className='prose m-auto max-w-none xl:w-3/4'>
                <RenderContent content={post.content} />
              </div>
            </section>
          </section>
          <section
            id='linkedBooks'
            className='col-span-4 row-start-4 lg:row-start-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-8 gap-4'>
            <h2 className='col-span-full header text-4xl text-white'>
              Linked Books
            </h2>
            {linkedBooks?.map((b) => (
              <BookCard
                key={b.isbn}
                book={b}
                className='bg-green-very-light p-2 rounded'
              />
            ))}
          </section>
        </article>
      </Layout>
    </>
  );
};

export default PostPage;
